import { useState } from 'react';
import { Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import FormInput from 'components/forms/FormInput';
import FormSelect, { type FormSelectOption } from 'components/forms/FormSelect';
import { parsePrice } from './model/parseImportCsv';
import type { ImportProductItem, ImportVendorLookup } from './model/types';
import { useSettings } from 'context/SettingsContext';
import { getCurrencyI18nParams, getPrimaryCurrencyPrefix } from 'utils/helpers/currency/currencyHelpers';
import SolarIcon from 'components/icons/SolarIcon';

type ImportCsvEditField = 'prod_article' | 'prod_name' | 'prod_def_price' | 'vendor';

interface ImportCsvEditingCell {
    index: number;
    field: ImportCsvEditField;
}

interface ImportCsvTableProps {
    items: ImportProductItem[];
    vendors: ImportVendorLookup[];
    allVendors: ImportVendorLookup[];
    onItemsChange: (items: ImportProductItem[]) => void;
    onClear: () => void;
}

const formatPrice = (price: number | null) => (price === null ? '—' : price.toFixed(2));

const formatVendor = (item: ImportProductItem) => {
    if (item.vendorId !== null && item.vendorName) {
        return item.vendorName;
    }

    if (item.vendorId !== null) {
        return String(item.vendorId);
    }

    return '—';
};

const buildVendorOptions = (
    vendors: ImportVendorLookup[],
    allVendors: ImportVendorLookup[],
    currentVendorId: number | null,
): FormSelectOption[] => {
    const options = vendors.map((vendor) => ({
        value: vendor.ID,
        label: vendor.vendorName,
    }));

    if (currentVendorId !== null && !vendors.some((vendor) => vendor.ID === currentVendorId)) {
        const extraVendor = allVendors.find((vendor) => vendor.ID === currentVendorId);
        if (extraVendor) {
            options.push({ value: extraVendor.ID, label: extraVendor.vendorName });
        }
    }

    return options;
};

const ImportCsvTable = ({ items, vendors, allVendors, onItemsChange, onClear }: ImportCsvTableProps) => {
    const { t } = useTranslation(['Products', 'common']);
    const { localSettings } = useSettings();
    const rate = Number(localSettings?.RATE?.current ?? 1) || 1;
    const [editing, setEditing] = useState<ImportCsvEditingCell | null>(null);

    const updateItem = (index: number, patch: Partial<ImportProductItem>) => {
        onItemsChange(items.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)));
    };

    const beginEdit = (index: number, field: ImportCsvEditField) => {
        setEditing({ index, field });
    };

    const cancelEdit = () => {
        setEditing(null);
    };

    const saveTextField = (index: number, field: 'prod_article' | 'prod_name', value: string | number) => {
        updateItem(index, { [field]: String(value).trim() });
        cancelEdit();
    };

    const savePriceField = (index: number, value: string | number) => {
        updateItem(index, { prodDefPrice: parsePrice(String(value)) });
        cancelEdit();
    };

    const saveVendorField = (index: number, vendorId: number | null) => {
        const vendor = allVendors.find((v) => v.ID === vendorId);

        updateItem(index, {
            vendorId: vendorId,
            vendorName: vendor?.vendorName ?? null,
            vendorRaw: vendorId === null ? '' : String(vendorId),
        });
        cancelEdit();
    };

    const handleDeleteRow = (index: number) => {
        const nextItems = items
            .filter((_, itemIndex) => itemIndex !== index)
            .map((item, itemIndex) => ({
                ...item,
                line: itemIndex + 1,
            }));
        onItemsChange(nextItems);
        if (editing?.index === index) {
            cancelEdit();
            return;
        }
        if (editing?.index !== undefined && editing.index > index) {
            setEditing({ ...editing, index: editing.index - 1 });
        }
    };

    const renderEditableCell = (
        index: number,
        field: 'prod_article' | 'prod_name' | 'prod_def_price',
        displayValue: string,
        inputType: string,
        onSave: (value: string | number) => void,
        numbersOnly = false,
        decimalPlaces?: number,
        prefix?: string,
    ) => {
        const isEditing = editing?.index === index && editing.field === field;

        if (isEditing) {
            return (
                <FormInput
                    key={`${index}-${field}`}
                    compact
                    showForm
                    numbersOnly={numbersOnly}
                    decimalPlaces={decimalPlaces}
                    prefix={prefix}
                    setShowForm={(show) => {
                        if (!show) cancelEdit();
                    }}
                    defaultValue={displayValue === '—' ? '' : displayValue}
                    type={inputType}
                    as="input"
                    callBack={onSave}
                />
            );
        }

        return (
            <span
                role="button"
                tabIndex={0}
                className="import-csv-editable"
                onClick={() => beginEdit(index, field)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        beginEdit(index, field);
                    }
                }}
            >
                {displayValue}
            </span>
        );
    };

    return (
        <div className="mt-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="text-muted small">{t('importCsv.rowsCount', { count: items.length })}</div>
                <button type="button" className="btn btn-link btn-sm text-danger p-0" onClick={onClear}>
                    <SolarIcon name="trash3" className="me-1" />
                    {t('importCsv.clear')}
                </button>
            </div>
            <div className="overflow-auto">
                <Table size="sm" className="ImportCsvTable mb-0" hover>
                    <thead>
                        <tr className="bg-light">
                            <td className="head-line">#</td>
                            <td className="head-article">{t('fields.article')}</td>
                            <td className="head-name">{t('fields.name')}</td>
                            <td className="head-price text-end">
                                {t('importCsv.colPrice', getCurrencyI18nParams())}
                            </td>
                            <td className="head-vendor">{t('fields.vendor')}</td>
                            <td className="head-actions text-end">{t('fields.actions')}</td>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => {
                            const isVendorEditing = editing?.index === index && editing.field === 'vendor';

                            return (
                                <tr key={`${item.line}-${item.prodArticle}-${index}`}>
                                    <td className="line text-muted">{item.line}</td>
                                    <td className="article">
                                        {renderEditableCell(
                                            index,
                                            'prod_article',
                                            item.prodArticle || '—',
                                            'text',
                                            (value) => saveTextField(index, 'prod_article', value),
                                        )}
                                    </td>
                                    <td className="name">
                                        {renderEditableCell(
                                            index,
                                            'prod_name',
                                            item.prodName || '—',
                                            'text',
                                            (value) => saveTextField(index, 'prod_name', value),
                                        )}
                                    </td>
                                    <td className="price text-end">
                                        {renderEditableCell(
                                            index,
                                            'prod_def_price',
                                            formatPrice(item.prodDefPrice),
                                            'text',
                                            (value) => savePriceField(index, value),
                                            true,
                                            2,
                                            getPrimaryCurrencyPrefix(rate),
                                        )}
                                    </td>
                                    <td className="vendor">
                                        {isVendorEditing ? (
                                            <FormSelect
                                                key={`${index}-vendor`}
                                                compact
                                                defaultValue={item.vendorId}
                                                options={buildVendorOptions(vendors, allVendors, item.vendorId)}
                                                emptyOptionLabel={t('messVendorNotSet')}
                                                setShowForm={(show) => {
                                                    if (!show) cancelEdit();
                                                }}
                                                callBack={(value) =>
                                                    saveVendorField(
                                                        index,
                                                        value === null || value === '' ? null : Number(value),
                                                    )
                                                }
                                            />
                                        ) : (
                                            <span
                                                role="button"
                                                tabIndex={0}
                                                className="import-csv-editable"
                                                onClick={() => beginEdit(index, 'vendor')}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        beginEdit(index, 'vendor');
                                                    }
                                                }}
                                            >
                                                {formatVendor(item)}
                                            </span>
                                        )}
                                    </td>
                                    <td className="actions text-end">
                                        <button
                                            type="button"
                                            className="btn btn-link btn-sm text-danger p-0"
                                            onClick={() => handleDeleteRow(index)}
                                            title={t('common:buttons.delete')}
                                        >
                                            <SolarIcon name="trash3" />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>
        </div>
    );
};

export default ImportCsvTable;
